/**
 * `getAuditByMandate(http, site_id, mandate_id)` — ADR-0120 §2.1(a)
 * deep-link helper. Resolves a shadow row's `mandate_id` to its
 * canonical `AuditRecord` (single-record shape) via the new
 *
 *   GET /v1/sites/:site_id/audit/by-mandate/:mandate_id
 *
 * route the backend Slice 1 lands.
 *
 * Wire shape mirrors `GET /v1/audit/:record_id`: a `{ record: AuditRecord
 * & { payload? } }` envelope. We map 404 → null so the caller's
 * loading-then-show-detail flow stays IDENTICAL to `MandateAPI.get()`
 * (per ADR-0120 §3.1: the existence-gate posture collapses cross-tenant,
 * unknown site, unknown mandate to the same 404 shape — the dashboard
 * receives `null` for all three and renders the same "not found" branch).
 *
 * Per ADR-0120 §4 security must-hold 3, this is the DASHBOARD pivot
 * route — the standard audit-record shape (intent_summary, settlement,
 * connector_call, origin_match) and the inspectable payload that
 * `MandateDetail` already renders. Buyer PII does NOT flow through this
 * route — buyer reveal stays on the existing operator-click
 * `BuyerDetailPanel` inside `MandateDetail`, which targets the
 * separately-rate-limited `/v1/audit/:record_id/buyer-detail` endpoint.
 *
 * Per ADR-0120 §4 security must-hold 4 the backend Slice 1 lands a
 * per-account rate limit of 120/minute on this route. A 429 surfaces as
 * the http-client's `ApiError('rate_limited', …)`; the caller renders
 * a non-blocking retry indicator the same way the shadow-log fetch does.
 * This function does NOT swallow `rate_limited` — it propagates so the
 * panel can surface a visible error pill (per ADR-0120 §2.1(c)).
 *
 * This helper does NOT need a `MandateAPI` interface change in
 * `@sill/types` (per ADR-0120 §3.1 last paragraph) — it lives next to
 * `real-mandates.ts` as a standalone function the dashboard imports
 * directly. Matches the precedent set by `audit-buyer-detail.ts`.
 */

import type { AuditRecord, AuditRecordPayload, MandateId, SiteId } from '@sill/types';

import { ApiError, type HttpClient } from './http-client.js';

interface AuditByMandateResponse {
  record: AuditRecord & {
    payload?: AuditRecordPayload;
  };
}

/**
 * Result of `getAuditByMandate`. The single-record route projects an
 * optional `payload` next to the audit record (same wire shape as
 * `GET /v1/audit/:record_id`). The caller spreads the payload into
 * `MandateDetail` when defined — mirroring the Mandates view at
 * `Mandates.tsx:1019`.
 */
export interface AuditByMandate {
  readonly record: AuditRecord;
  readonly payload?: AuditRecordPayload;
}

/**
 * Resolve a shadow row's `mandate_id` to the canonical audit record for
 * the same `site_id`. Returns `null` on 404 (cross-tenant / unknown site
 * / unknown mandate — all collapse to the same shape per the existence-
 * gate posture in ADR-0120 §3.1).
 *
 * Throws for any other `ApiError` (notably `rate_limited` 429 and
 * `server_error` 5xx) so the panel surfaces a visible error rather
 * than silently rendering an empty detail view. The caller renders a
 * non-blocking retry indicator.
 */
export async function getAuditByMandate(
  http: HttpClient,
  site_id: SiteId,
  mandate_id: MandateId,
): Promise<AuditByMandate | null> {
  try {
    const res = await http.request<AuditByMandateResponse>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/audit/by-mandate/${encodeURIComponent(
        mandate_id,
      )}`,
    });
    if (res === undefined || res.record === undefined) {
      throw new ApiError('bad_response', null);
    }
    const out: AuditByMandate = { record: res.record };
    if (res.record.payload !== undefined) {
      return { ...out, payload: res.record.payload };
    }
    return out;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') {
      return null;
    }
    throw err;
  }
}
